"use client";
import { useState } from "react";
import { AnimatedGradientBackground, GradientPresets } from "@/components/Animation/GradientBackground";
import IconBox from "@/components/Icons/iconbox";
import {
  Award,
  Target,
  TrendingUp,
  Users,
  Lightbulb,
  Heart,
  Cloud,
  Database,
  Globe,
  Zap,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

interface PhilosophyPoint {
  icon: any;
  title: string;
  description: string;
}

interface CultureValue {
  icon: any;
  title: string;
  description: string;
}

interface Tech {
  name: string;
  icon: any;
}

const philosophyPoints: PhilosophyPoint[] = [
  {
    icon: Target,
    title: "Clear Design",
    description:
      "We design for clarity: clear goals, clear delivery pathways, clear accountability. Our clients trust us not only for our expertise but for our transparency and the confidence we bring to complex environments. Every project begins with clarity on what success looks like.",
  },
  {
    icon: TrendingUp,
    title: "Truth Over Trends",
    description:
      "Where others chase trends, we chase truth: what works, what scales, and what sustains. We focus on proven methods backed by evidence, not the latest buzzwords. Our recommendations are grounded in real-world results and long-term viability.",
  },
  {
    icon: Users,
    title: "Beyond Technology",
    description:
      "Achieving alignment among systems, incentives, and people toward a shared objective goes beyond implementing new technology. True transformation happens when strategy, systems, and culture move in harmony. Technology is the enabler, not the answer.",
  },
];

const cultureValues: CultureValue[] = [
  {
    icon: Lightbulb,
    title: "Curiosity Fuels Us",
    description:
      "We celebrate diversity of thought and encourage experimentation, because that's where new insight emerges. Our teams are empowered to challenge assumptions, test hypotheses, and learn from both successes and failures.",
  },
  {
    icon: Users,
    title: "Multidisciplinary Teams",
    description:
      "Our teams span strategists, data scientists, product designers, and engineers, united by a shared belief that change is both art and science. This diversity creates innovative solutions that single-discipline teams miss.",
  },
  {
    icon: Heart,
    title: "People First",
    description:
      "We invest in professional development and psychological health equally. When our people thrive, so do our clients. This isn't just policy it's embedded in how we structure work, celebrate wins, and support each other.",
  },
];

const ecosystemTech: Tech[] = [
  { name: "AWS", icon: Cloud },
  { name: "Databricks", icon: Database },
  { name: "Snowflake", icon: Database },
  { name: "ServiceNow", icon: Globe },
  { name: "MuleSoft", icon: Zap },
  { name: "Salesforce", icon: Globe },
];

export default function AboutPage() {
  const [expandedStory, setExpandedStory] = useState(true);
  const [activeValue, setActiveValue] = useState<number | null>(null);

  return (
    <main className="min-h-screen text-text-primary">
      <AnimatedGradientBackground {...GradientPresets.about} />

      {/* HERO */}
      <section className="relative max-w-6xl mx-auto px-6">
        <div className="border border-standard pt-40 p-8 sm:p-12 relative overflow-hidden transition-all duration-1000 ease-[cubic-bezier(0.19,1,0.22,1)] opacity-100 translate-y-0">
          <div className="absolute inset-0 -z-10">
          </div>

          <h1 className="h1 mt-24 mb-6">
            Why <span className="text-text-secondary">Luminar</span>
          </h1>
          <p className="body-lg mb-6">
            Built for the AI age. Grounded in human-centred design. Luminar is a management
            consultancy that believes modernisation is most powerful when human insight, data
            intelligence, and disciplined execution come together.
          </p>
          <p className="body-md">That belief is what built Luminar.</p>
        </div>
      </section>

      {/* OUR STORY */}
      <section className="max-w-6xl mx-auto px-6 mt-16 sm:mt-24">
        <div className="card-glass">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <IconBox icon={Award} index={0} variant="badge" size="md" />
              <h2 className="h2">The Gap We Bridge</h2>
            </div>
            <button
              onClick={() => setExpandedStory(!expandedStory)}
              className="flex items-center gap-2 body-md cursor-pointer hover:text-accentcolourteal transition-colors"
            >
              {expandedStory ? "Show Less" : "Read More"}
              {expandedStory ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </button>
          </div>

          {!expandedStory ? (
            <p className="body-lg">
              Luminar was founded by delivery practitioners who had seen too many strategies stall
              between PowerPoint and production.
            </p>
          ) : (
            <div className="space-y-6">
              <p className="body-lg">
                Luminar was founded by delivery practitioners who had seen too many strategies
                stall between PowerPoint and production.
              </p>
              <p className="body-md">
                Having led major programmes inside global organisations, our founders built Luminar
                to close this gap blending strategic advisory, delivery excellence, and deep
                technical capability within one integrated model.
              </p>
              <p className="body-md">
                Every engagement is guided by curiosity, precision, and accountability because real
                change demands all three.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* FOUNDER QUOTE */}
      <section className="max-w-6xl mx-auto px-6 mt-16 sm:mt-24">
        <div className="border-l-4 border-accentcolourteal backdrop-blur-sm pl-8 sm:pl-12 pr-6 sm:pr-8 py-8 sm:py-10">
          <p className="text-2xl sm:text-3xl md:text-4xl leading-relaxed text-text-primary mb-6">
            "Real progress happens when three things align: strategy that's grounded in systems
            thinking, technology that truly serves people, and delivery that's measured by actual
            outcomes."
          </p>
          <p className="body-md ml-0 sm:ml-4">— Mohsin Khan, Founder & Managing Director</p>
        </div>
      </section>

      {/* OUR PHILOSOPHY */}
      <section className="max-w-6xl mx-auto px-6 mt-16 sm:mt-24">
        <h2 className="h2 text-center mb-8 sm:mb-12">Our Philosophy</h2>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
          {philosophyPoints.map((point, i) => (
            <div
              key={i}
              className="group relative border border-standard backdrop-blur-sm p-6 sm:p-8 overflow-hidden"
            >
              {/* Decorative corner accent */}
              <div
                className={`absolute top-0 left-0 w-32 h-32 opacity-15 ${
                  i % 2 === 0 ? "bg-accentcolourteal" : "bg-accentcolourorange"
                }`}
              />

              {/* Icon */}
              <div className="relative mb-4 sm:mb-6">
                <IconBox icon={point.icon} index={i} variant="badge" size="md" />
              </div>

              {/* Content */}
              <h3 className="h3 mb-4">{point.title}</h3>
              <p className="body-md leading-relaxed">{point.description}</p>

              {/* Bottom accent line */}
              <div
                className={`mt-6 h-1 w-full ${
                  i % 2 === 0 ? "bg-accentcolourteal" : "bg-accentcolourorange"
                }`}
              />
            </div>
          ))}
        </div>
      </section>

      {/* OUR CULTURE */}
      <section className="max-w-6xl mx-auto px-6 mt-16 sm:mt-24">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="h2 mb-4">Our Culture</h2>
        </div>

        <div className="space-y-4">
          {cultureValues.map((value, i) => {
            const isActive = activeValue === i;

            return (
              <div
                key={i}
                className="border border-standard backdrop-blur-sm overflow-hidden transition-all hover:border-accentcolourteal"
              >
                <button
                  onClick={() => setActiveValue(isActive ? null : i)}
                  className="w-full flex items-center justify-between p-6 sm:p-8 text-left group transition-all"
                >
                  <div className="flex items-center gap-4 sm:gap-6">
                    <div className="transition-transform group-hover:scale-110">
                      <IconBox icon={value.icon} index={i} variant="badge" size="md" />
                    </div>
                    <h3 className="h3">{value.title}</h3>
                  </div>
                  <div
                    className={`flex-shrink-0 transition-transform duration-300 ${
                      isActive ? "rotate-180" : ""
                    }`}
                  >
                    <ChevronDown className="w-6 h-6 sm:w-8 sm:h-8 text-accentcolourteal" />
                  </div>
                </button>

                {/* Expandable content */}
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    isActive ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                  }`}
                >
                  <div className="px-6 sm:px-8 pb-6 sm:pb-8 sm:ml-20">
                    <div
                      className={`h-1 w-12 mb-4 sm:mb-6 ${
                        i % 2 === 0 ? "bg-accentcolourorange" : "bg-accentcolourteal"
                      }`}
                    />
                    <p className="body-lg leading-relaxed">{value.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* OUR ECOSYSTEM */}
      <section className="max-w-6xl mx-auto px-6 mt-16 sm:mt-24 mb-16 sm:mb-24">
        <div className="card-glass">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="h2 mb-4 sm:mb-6">Our Ecosystem</h2>
            <p className="body-lg max-w-3xl mx-auto">
              Luminar works across technology ecosystems, not within silos.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6 mt-8 sm:mt-12">
            {ecosystemTech.map((tech, i) => (
              <div
                key={i}
                className="group flex flex-col items-center justify-center backdrop-blur-sm"
              >
                <IconBox icon={tech.icon} index={0} size="md" variant="badge" className="mb-2" />
                <p className="body-md text-center">{tech.name}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 sm:mt-12 p-6 sm:p-8 border-l-4 border-accentcolourteal backdrop-blur-sm">
            <p className="body-md">
              <strong className="text-text-primary">Vendor-agnostic by design,</strong> Luminar selects technologies that fit
              each client's context. This flexibility allows us to operate seamlessly across
              regulated industries and global delivery landscapes.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}